import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trans } from '../../components/i18n';
import Button from '../../components/ui/Button';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/ui/Input';
import Checkbox from '../../components/ui/Checkbox';
import { loginByEmail } from '../../services/auth';
import { useAuth } from '../../hooks/useAuth';
import Banner from '../../components/ui/Banner';

const AuthEmailPage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [rememberMe, setRememberMe] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const { authenticate } = useAuth();

	document.title = `${t('general.app_name')} | ${t('general.login')}`;

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError(null);

		try {
			const response = await loginByEmail(email, password);
			const user = response.user;

			if (response.status === 'success') {
				const hasAccess = response.addOnList.some((addOn) => addOn.type === 'SOCRATES');
				if (hasAccess) {
					authenticate(user, response.accessKey, rememberMe);
					navigate('/');
				} else {
					setError('login.no_access');
				}
			} else if (response.status === 'failed') {
				setError('login.invalid_credentials');
			} else {
				setError('login.generic_error');
			}
		} catch (err) {
			setError('login.connection_error');
			console.error(err);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className='flex flex-col flex-1 justify-center items-center scroll-y'>
			<div className='flex flex-col max-w-md w-full'>
				<h3 className='text-3xl font-bold '>
					<Trans i18nKey='login.right_title' values={{ app_name: t('general.app_name') }} />
				</h3>
				<p className='text-sm text-gray-500 mt-3'>
					<Trans
						i18nKey='login.right_description'
						values={{ app_name: t('general.app_name') }}
						components={{ bold: <span className='font-bold' /> }}
					/>
				</p>
				<form className='flex flex-col mt-10' onSubmit={handleSubmit}>
					<Input
						label={t('login.email')}
						type='email'
						icon='ri-mail-open-line'
						placeholder={t('login.email_placeholder', { app_name: t('general.app_name').toLowerCase() })}
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						errorText={t('login.email_error')}
						validation={
							/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
						}
						autoComplete='email'
					/>
					<Input
						label={t('login.password')}
						type='password'
						placeholder={t('login.password_placeholder')}
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						errorText={t('login.password_error')}
						validation={/^\d*[a-zA-Z][a-zA-Z0-9]*$/}
						autoComplete='current-password'
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								handleSubmit(e);
							}
						}}
					/>
					<div className='flex justify-between items-center my-10'>
						<Checkbox
							label={t('login.remember_me')}
							checked={rememberMe}
							onChange={(e) => setRememberMe(e.target.checked)}
						/>
						<Button
							type='text'
							size='medium'
							variant='secondary'
							onClick={() => navigate('/login/forgot-password')}
						>
							<Trans i18nKey='login.forgot_password' />
						</Button>
					</div>
					{error && (
						<Banner
							label={t(error, { app_name: t('general.app_name') })}
							variant='error'
							className='mb-4'
						/>
					)}
					<Button
						type='solid'
						size='large'
						variant='primary'
						mode='submit'
						disabled={!email || !password}
						onClick={handleSubmit}
						loading={loading}
					>
						<Trans i18nKey='login.login_button' />
					</Button>
					<Button
						className='mt-10 text-left w-fit'
						type='text'
						size='medium'
						variant='secondary'
						onClick={() => navigate('/login')}
					>
						<i className='ri-arrow-left-line hover:text-decoration-none' />
						<Trans i18nKey='login.go_back' />
					</Button>
				</form>
			</div>
		</div>
	);
};

export default AuthEmailPage;
